import React, { useState, useEffect } from 'react';
import { Modal, TextField, Button, Typography, Card, CardContent, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, IconButton, Snackbar, CircularProgress, Grid, Paper, Box, Chip, Divider } from '@material-ui/core';
import { makeStyles, createTheme, ThemeProvider } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import StorageIcon from '@material-ui/icons/Storage';
import RestaurantIcon from '@material-ui/icons/Restaurant';
import EcoIcon from '@material-ui/icons/Eco';
import { useParams } from 'react-router-dom';
import AdminNavbar from './AdminNavbar';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useNavigate } from 'react-router-dom';

const theme = createTheme({
    palette: {
        primary: {
            main: '#059669',
            light: '#10b981',
            dark: '#047857',
        },
        secondary: {
            main: '#dc2626',
            light: '#ef4444',
            dark: '#b91c1c',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h4: {
            fontWeight: 700,
            color: '#1e293b',
        },
        h6: {
            fontWeight: 600,
            color: '#334155',
        },
        body1: {
            color: '#64748b',
        },
        body2: {
            color: '#64748b',
        },
    },
    shape: {
        borderRadius: 12,
    },
});

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        paddingBottom: theme.spacing(4),
    },
    // Masquer le menu hamburger de AdminNavbar
    navbar: {
        '& .MuiIconButton-root:first-child': {
            display: 'none !important',
        },
    },
    header: {
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        color: 'white',
        padding: theme.spacing(4, 3),
        borderRadius: '0 0 24px 24px',
        marginBottom: theme.spacing(4),
        boxShadow: '0 10px 40px rgba(5, 150, 105, 0.2)',
    },
    headerContent: {
        maxWidth: 1200,
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    headerTitleText: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    backButton: {
        color: 'white',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1, 2),
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            transform: 'translateY(-2px)',
        },
    },
    headerIcon: {
        fontSize: '4rem',
        opacity: 0.3,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    mainContainer: {
        maxWidth: 1200,
        margin: '0 auto',
        padding: theme.spacing(0, 3),
    },
    infoSection: {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(3),
        marginBottom: theme.spacing(4),
        border: '1px solid #bbf7d0',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    infoContent: {
        flex: 1,
    },
    infoTitle: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#14532d',
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    infoText: {
        color: '#166534',
        fontSize: '1rem',
        lineHeight: 1.6,
    },
    formSection: {
        background: 'white',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(4),
        marginBottom: theme.spacing(4),
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(226, 232, 240, 0.5)',
    },
    sectionTitle: {
        fontSize: '1.5rem',
        fontWeight: 600,
        color: '#1e293b',
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    sectionSubtitle: {
        color: '#64748b',
        fontSize: '1rem',
        marginBottom: theme.spacing(3),
        lineHeight: 1.6,
    },
    textField: {
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.shape.borderRadius,
            transition: 'all 0.3s ease',
            '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.light,
                },
            },
            '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                },
            },
        },
        '& .MuiInputLabel-root': {
            fontWeight: 500,
        },
    },
    radioGroup: {
        flexDirection: 'row',
        gap: theme.spacing(3),
        marginTop: theme.spacing(1),
    },
    radioOption: {
        marginRight: 0,
        padding: theme.spacing(2),
        border: '2px solid #e2e8f0',
        borderRadius: theme.shape.borderRadius,
        transition: 'all 0.3s ease',
        '&:hover': {
            borderColor: theme.palette.primary.light,
            backgroundColor: 'rgba(5, 150, 105, 0.05)',
        },
        '& .MuiFormControlLabel-label': {
            fontWeight: 500,
        },
    },
    radioOptionSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: 'rgba(5, 150, 105, 0.1)',
    },
    addButton: {
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        padding: theme.spacing(1.5, 4),
        borderRadius: theme.shape.borderRadius,
        fontWeight: 600,
        fontSize: '1.1rem',
        textTransform: 'none',
        boxShadow: '0 8px 25px rgba(5, 150, 105, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 35px rgba(5, 150, 105, 0.4)',
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, #065f46 100%)`,
        },
    },
    containerGrid: {
        marginTop: theme.spacing(4),
    },
    containerCard: {
        height: '100%',
        borderRadius: theme.shape.borderRadius,
        transition: 'all 0.3s ease',
        border: '1px solid rgba(226, 232, 240, 0.5)',
        overflow: 'hidden',
        '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            borderColor: theme.palette.primary.light,
        },
    },
    containerImage: {
        width: '100%',
        height: 180,
        objectFit: 'cover',
        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    },
    containerInfo: {
        padding: theme.spacing(2.5),
    },
    containerName: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#1e293b',
        marginBottom: theme.spacing(1),
    },
    containerDetail: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5),
        padding: theme.spacing(0.5, 0),
    },
    containerLabel: {
        fontWeight: 500,
        color: '#64748b',
        fontSize: '0.9rem',
    },
    containerValue: {
        fontWeight: 600,
        color: '#334155',
        fontSize: '0.9rem',
    },
    stockChip: {
        fontWeight: 600,
        fontSize: '0.8rem',
    },
    stockLow: {
        backgroundColor: '#fee2e2',
        color: '#dc2626',
    },
    stockMedium: {
        backgroundColor: '#fef3c7',
        color: '#d97706',
    },
    stockHigh: {
        backgroundColor: '#dcfce7',
        color: '#16a34a',
    },
    priceTag: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        fontWeight: 600,
        fontSize: '1rem',
        padding: theme.spacing(1, 2),
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(1),
    },
    actionButtons: {
        display: 'flex',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        padding: theme.spacing(0, 2.5, 2.5),
    },
    editButton: {
        flex: 1,
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        padding: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
        fontWeight: 500,
        fontSize: '0.9rem',
        textTransform: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'translateY(-1px)',
        },
    },
    deleteButton: {
        flex: 1,
        backgroundColor: theme.palette.secondary.main,
        color: 'white',
        padding: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
        fontWeight: 500,
        fontSize: '0.9rem',
        textTransform: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
            transform: 'translateY(-1px)',
        },
    },
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(4),
        maxWidth: 600,
        width: '100%',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2)',
        border: 'none',
        outline: 'none',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
    },
    modalTitle: {
        fontSize: '1.5rem',
        fontWeight: 600,
        color: '#1e293b',
    },
    closeButton: {
        color: '#64748b',
        transition: 'all 0.3s ease',
        '&:hover': {
            color: theme.palette.secondary.main,
            backgroundColor: 'rgba(220, 38, 38, 0.1)',
        },
    },
    modalActions: {
        display: 'flex',
        gap: theme.spacing(2),
        marginTop: theme.spacing(3),
        justifyContent: 'flex-end',
    },
    modalButton: {
        padding: theme.spacing(1.5, 3),
        borderRadius: theme.shape.borderRadius,
        fontWeight: 600,
        textTransform: 'none',
        transition: 'all 0.3s ease',
    },
    cancelButton: {
        backgroundColor: '#f1f5f9',
        color: '#64748b',
        '&:hover': {
            backgroundColor: '#e2e8f0',
            transform: 'translateY(-1px)',
        },
    },
    saveButton: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'translateY(-1px)',
        },
    },
    progressButton: {
        position: 'relative',
    },
    buttonProgress: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(6),
        color: '#64748b',
    },
    emptyIcon: {
        fontSize: '4rem',
        marginBottom: theme.spacing(2),
        opacity: 0.5,
    },
    divider: {
        margin: theme.spacing(3, 0),
        backgroundColor: '#e2e8f0',
    },
}));

function CreateContainerComponent({ open, onClose, onAdd }) {
    const classes = useStyles();
    const [brand, setBrand] = useState('');
    const [name, setName] = useState('');
    const [volume, setVolume] = useState('');
    const [material, setMaterial] = useState('');
    const [image, setImage] = useState(null);
    const [containers, setContainers] = useState([]);
    const [containerType, setContainerType] = useState('gourde');
    const { establishmentId } = useParams();
    const [initialStock, setInitialStock] = useState('');
    const [threshold, setThreshold] = useState('');
    const [stockModalOpen, setStockModalOpen] = useState(false);
    const [selectedContainer, setSelectedContainer] = useState(null);
    const [stockChange, setStockChange] = useState(0);
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [price, setPrice] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [editName, setEditName] = useState('');
    const [editBrand, setEditBrand] = useState('');
    const [editVolume, setEditVolume] = useState('');
    const [editMaterial, setEditMaterial] = useState('');
    const [editPrice, setEditPrice] = useState('');
    
    const navigate = useNavigate();

    const handleEditClick = (container) => {
        setSelectedContainer(container);
        setEditName(container.name || '');
        setEditBrand(container.brand || '');
        setEditVolume(container.volume || '');
        setEditMaterial(container.material || '');
        setEditPrice(container.price || '');
        setEditMode(true);
        setStockModalOpen(true);
    };

    const resetEditMode = () => {
        setEditMode(false);
        setEditName('');
        setEditBrand('');
        setEditVolume('');
        setEditMaterial('');
        setEditPrice('');
    };

    const handleCloseStockModal = () => {
        setStockModalOpen(false);
        setSelectedContainer(null);
        setStockChange(0);
        resetEditMode();
    };

    useEffect(() => {
        const fetchContainers = async () => {
            try {
                const response = await fetch(`https://backend.csibon.app/container_chr/containers/establishment/${establishmentId}`);
                if (!response.ok) {
                    throw new Error('Erreur réseau lors de la récupération des contenants.');
                }
                const containers = await response.json();
                setContainers(containers);
            } catch (error) {
                console.error('Erreur lors de la récupération des contenants:', error);
            }
        };
        fetchContainers();
    }, [establishmentId]);

    const handleSaveClick = async () => {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('brand', brand);
        formData.append('volume', volume);
        formData.append('material', material);
        formData.append('id_etablissement', establishmentId);
        formData.append('containerType', containerType);
        formData.append('initialStock', initialStock);
        formData.append('threshold', threshold);
        formData.append('price', price);
        if (image) {
            formData.append('file', image);
        }

        try {
            const response = await fetch('https://backend.csibon.app/container_chr/containers', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Erreur réseau lors de l\'envoi du conteneur.');
            }

            const result = await response.json();
            setContainers([...containers, result.container]);

            // Réinitialiser les champs
            setBrand('');
            setName('');
            setVolume('');
            setMaterial('');
            setPrice('');
            setImage(null);
            setContainerType('gourde');
            setInitialStock('');
            setThreshold('');
            
            setSnackbarMessage('Contenant ajouté avec succès !');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Erreur lors de l\'ajout du conteneur:', error);
            setSnackbarMessage('Erreur lors de l\'ajout du contenant');
            setSnackbarOpen(true);
        }
    };

    const handleDeleteClick = async (containerId) => {
        try {
            const response = await fetch(`https://backend.csibon.app/container_chr/containers/${containerId}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error('Erreur réseau lors de la suppression du contenant.');
            }

            setContainers(containers.filter(container => container.id !== containerId));
            setSnackbarMessage('Contenant supprimé avec succès !');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Erreur lors de la suppression du contenant:', error);
            setSnackbarMessage('Erreur lors de la suppression');
            setSnackbarOpen(true);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const getStockStatus = (currentStock, threshold) => {
        if (currentStock <= threshold) return 'low';
        if (currentStock <= threshold * 2) return 'medium';
        return 'high';
    };

    const getContainerTypeIcon = (type) => {
        switch (type) {
            case 'gourde': return '🚰';
            case 'mug': return '☕';
            case 'box': return '📦';
            default: return '📦';
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <div className={classes.root}>
                <div className={classes.navbar}>
                    <AdminNavbar establishmentId={establishmentId} name={""} />
                </div>
                
                {/* Header */}
                <div className={classes.header}>
                    <div className={classes.headerContent}>
                        <div className={classes.headerTitle}>
                            <Button
                                onClick={() => navigate(`/accueil`)}
                                className={classes.backButton}
                                startIcon={<ArrowBackIcon />}
                            >
                                Retour
                            </Button>
                            <div className={classes.headerTitleText}>
                                <Typography variant="h4">
                                    Contenants Réutilisables CHR
                                </Typography>
                                <Typography variant="body1" style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '1.1rem' }}>
                                    Gérez vos contenants mis à disposition des clients
                                </Typography>
                            </div>
                        </div>
                        <div className={classes.headerIcon}>
                            <RestaurantIcon style={{ fontSize: '3rem' }} />
                            <EcoIcon style={{ fontSize: '2rem' }} />
                        </div>
                    </div>
                </div>

                <div className={classes.mainContainer}>
                    {/* Section d'information */}
                    <Paper className={classes.infoSection}>
                        <EcoIcon style={{ fontSize: '3rem', color: '#059669' }} />
                        <div className={classes.infoContent}>
                            <Typography className={classes.infoTitle}>
                                <RestaurantIcon />
                                Contenants pour votre établissement CHR
                            </Typography>
                            <Typography className={classes.infoText}>
                                Ajoutez et gérez les contenants réutilisables que vous mettez à disposition de vos clients 
                                (gourdes, mugs, box de livraison, etc.). Ces contenants permettent de réduire les déchets 
                                et d'offrir une solution écologique à votre clientèle.
                            </Typography>
                        </div>
                    </Paper>

                    {/* Formulaire d'ajout */}
                    <Paper className={classes.formSection}>
                        <Typography className={classes.sectionTitle}>
                            <AddIcon />
                            Ajouter un contenant à votre stock
                        </Typography>
                        <Typography className={classes.sectionSubtitle}>
                            Enregistrez les contenants réutilisables que vous proposez à vos clients. 
                            Définissez le stock initial et le seuil d'alerte pour un suivi optimal.
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Nom du contenant *"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    className={classes.textField}
                                    placeholder="Ex: Gobelet réutilisable EcoCup"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Marque"
                                    value={brand}
                                    onChange={(e) => setBrand(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    className={classes.textField}
                                    placeholder="Ex: EcoCup, Keepcup, etc."
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Volume *"
                                    value={volume}
                                    onChange={(e) => setVolume(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    className={classes.textField}
                                    placeholder="Ex: 250ml, 33cl, 500ml"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Matériau"
                                    value={material}
                                    onChange={(e) => setMaterial(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    className={classes.textField}
                                    placeholder="Ex: Plastique recyclé, Verre, Inox"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Prix de vente (€)"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    type="number"
                                    className={classes.textField}
                                    placeholder="5.00"
                                    helperText="Prix de vente du contenant à vos clients"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Stock initial *"
                                    value={initialStock}
                                    onChange={(e) => setInitialStock(e.target.value)}
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    className={classes.textField}
                                    placeholder="100"
                                    helperText="Nombre de contenants en stock actuellement"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Seuil d'alerte *"
                                    value={threshold}
                                    onChange={(e) => setThreshold(e.target.value)}
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    className={classes.textField}
                                    placeholder="20"
                                    helperText="Stock minimum avant alerte de réapprovisionnement"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    type="file"
                                    onChange={(e) => setImage(e.target.files[0])}
                                    fullWidth
                                    variant="outlined"
                                    className={classes.textField}
                                    inputProps={{ accept: "image/*" }}
                                    helperText="Photo du contenant pour faciliter l'identification"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend" style={{ fontWeight: 600, color: '#334155', marginBottom: 16 }}>
                                        Type de contenant *
                                    </FormLabel>
                                    <RadioGroup
                                        row
                                        value={containerType}
                                        onChange={(e) => setContainerType(e.target.value)}
                                        className={classes.radioGroup}
                                    >
                                        <FormControlLabel 
                                            value="gourde" 
                                            control={<Radio color="primary" />} 
                                            label="🚰 Gourde / Bouteille"
                                            className={`${classes.radioOption} ${containerType === 'gourde' ? classes.radioOptionSelected : ''}`}
                                        />
                                        <FormControlLabel 
                                            value="mug" 
                                            control={<Radio color="primary" />} 
                                            label="☕ Mug / Gobelet"
                                            className={`${classes.radioOption} ${containerType === 'mug' ? classes.radioOptionSelected : ''}`}
                                        />
                                        <FormControlLabel 
                                            value="box" 
                                            control={<Radio color="primary" />} 
                                            label="📦 Box / Emballage alimentaire"
                                            className={`${classes.radioOption} ${containerType === 'box' ? classes.radioOptionSelected : ''}`}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Button 
                                    variant="contained" 
                                    onClick={handleSaveClick} 
                                    className={classes.addButton}
                                    startIcon={<AddIcon />}
                                    fullWidth
                                    size="large"
                                >
                                    Ajouter ce contenant à mon stock
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Liste des contenants */}
                    <Paper className={classes.formSection}>
                        <Typography className={classes.sectionTitle}>
                            <StorageIcon />
                            Vos contenants en stock ({containers.length})
                        </Typography>
                        <Typography className={classes.sectionSubtitle}>
                            Retrouvez tous les contenants réutilisables disponibles dans votre établissement. 
                            Vous pouvez modifier les informations et gérer les stocks en temps réel.
                        </Typography>
                        
                        {containers.length === 0 ? (
                            <div className={classes.emptyState}>
                                <div className={classes.emptyIcon}>🏪</div>
                                <Typography variant="h6" gutterBottom>
                                    Aucun contenant enregistré
                                </Typography>
                                <Typography variant="body2">
                                    Commencez par ajouter vos premiers contenants réutilisables en utilisant le formulaire ci-dessus. 
                                    Vos clients pourront ainsi profiter d'une solution écologique !
                                </Typography>
                            </div>
                        ) : (
                            <Grid container spacing={3} className={classes.containerGrid}>
                                {containers.map((container) => {
                                    const stockStatus = getStockStatus(container.current_stock, container.threshold);
                                    return (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={container.id}>
                                            <Card className={classes.containerCard}>
                                                <img 
                                                    src={`https://backend.csibon.app/static/container_images/${container.image_url}`} 
                                                    className={classes.containerImage}
                                               />
                                               <div className={classes.containerInfo}>
                                                   <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                                                       <span style={{ fontSize: '1.2rem' }}>
                                                           {getContainerTypeIcon(container.container_type)}
                                                       </span>
                                                       <Typography className={classes.containerName}>
                                                           {container.name}
                                                       </Typography>
                                                   </div>
                                                   
                                                   <div className={classes.containerDetail}>
                                                       <span className={classes.containerLabel}>Marque</span>
                                                       <span className={classes.containerValue}>{container.brand || 'Non spécifiée'}</span>
                                                   </div>
                                                   
                                                   <div className={classes.containerDetail}>
                                                       <span className={classes.containerLabel}>Volume</span>
                                                       <span className={classes.containerValue}>{container.volume}</span>
                                                   </div>
                                                   
                                                   <div className={classes.containerDetail}>
                                                       <span className={classes.containerLabel}>Matériau</span>
                                                       <span className={classes.containerValue}>{container.material || 'Non spécifié'}</span>
                                                   </div>
                                                   
                                                   <div className={classes.containerDetail}>
                                                       <span className={classes.containerLabel}>Stock disponible</span>
                                                       <Chip
                                                           label={`${container.current_stock} unités`}
                                                           size="small"
                                                           className={`${classes.stockChip} ${classes[`stock${stockStatus.charAt(0).toUpperCase() + stockStatus.slice(1)}`]}`}
                                                       />
                                                   </div>
                                                   
                                                   <div className={classes.containerDetail}>
                                                       <span className={classes.containerLabel}>Seuil d'alerte</span>
                                                       <span className={classes.containerValue}>{container.threshold} unités</span>
                                                   </div>
                                                   
                                                   {container.price && (
                                                       <Chip
                                                           label={`${container.price}€`}
                                                           className={classes.priceTag}
                                                       />
                                                   )}
                                               </div>
                                               
                                               <div className={classes.actionButtons}>
                                                   <Button
                                                       variant="contained"
                                                       className={classes.editButton}
                                                       startIcon={<EditIcon />}
                                                       onClick={() => handleEditClick(container)}
                                                   >
                                                       Modifier
                                                   </Button>
                                                   <Button
                                                       variant="contained"
                                                       className={classes.deleteButton}
                                                       startIcon={<DeleteIcon />}
                                                       onClick={() => handleDeleteClick(container.id)}
                                                   >
                                                       Supprimer
                                                   </Button>
                                               </div>
                                           </Card>
                                       </Grid>
                                   );
                               })}
                           </Grid>
                       )}
                   </Paper>
               </div>

               {/* Modal de modification */}
               <Modal open={stockModalOpen} onClose={handleCloseStockModal} className={classes.modal}>
                   <div className={classes.modalContent}>
                       <div className={classes.modalHeader}>
                           <Typography className={classes.modalTitle}>
                               Modifier {selectedContainer?.name}
                           </Typography>
                           <IconButton onClick={handleCloseStockModal} className={classes.closeButton}>
                               <CloseIcon />
                           </IconButton>
                       </div>
                       
                       <Typography variant="body2" style={{ color: '#64748b', marginBottom: 20 }}>
                           Modifiez les informations de ce contenant et ajustez le stock selon vos besoins.
                       </Typography>
                       
                       <Grid container spacing={2}>
                           <Grid item xs={12} sm={6}>
                               <TextField
                                   label="Nom du contenant"
                                   value={editName}
                                   onChange={(e) => setEditName(e.target.value)}
                                   fullWidth
                                   variant="outlined"
                                   className={classes.textField}
                               />
                           </Grid>
                           <Grid item xs={12} sm={6}>
                               <TextField
                                   label="Marque"
                                   value={editBrand}
                                   onChange={(e) => setEditBrand(e.target.value)}
                                   fullWidth
                                   variant="outlined"
                                   className={classes.textField}
                               />
                           </Grid>
                           <Grid item xs={12} sm={4}>
                               <TextField
                                   label="Volume"
                                   value={editVolume}
                                   onChange={(e) => setEditVolume(e.target.value)}
                                   fullWidth
                                   variant="outlined"
                                   className={classes.textField}
                               />
                           </Grid>
                           <Grid item xs={12} sm={4}>
                               <TextField
                                   label="Matériau"
                                   value={editMaterial}
                                   onChange={(e) => setEditMaterial(e.target.value)}
                                   fullWidth
                                   variant="outlined"
                                   className={classes.textField}
                               />
                           </Grid>
                           <Grid item xs={12} sm={4}>
                               <TextField
                                   label="Prix de vente (€)"
                                   value={editPrice}
                                   onChange={(e) => setEditPrice(e.target.value)}
                                   type="number"
                                   fullWidth
                                   variant="outlined"
                                   className={classes.textField}
                               />
                           </Grid>
                       </Grid>
                       
                       <Divider className={classes.divider} />
                       
                       <Typography variant="h6" gutterBottom style={{ color: '#334155', fontWeight: 600 }}>
                           Gestion du stock
                       </Typography>
                       <Typography variant="body2" style={{ color: '#64748b', marginBottom: 16 }}>
                           Stock actuel: <strong style={{ color: '#059669' }}>{selectedContainer?.current_stock} unités</strong>
                       </Typography>
                       
                       <TextField
                           label="Ajustement du stock"
                           value={stockChange}
                           onChange={(e) => setStockChange(Number(e.target.value))}
                           type="number"
                           fullWidth
                           variant="outlined"
                           className={classes.textField}
                           placeholder="Ex: +50 pour ajouter, -20 pour retirer"
                           helperText="Entrez un nombre positif pour ajouter du stock, négatif pour en retirer. Laissez 0 pour ne pas modifier le stock."
                       />
                       
                       <div className={classes.modalActions}>
                           <Button
                               onClick={handleCloseStockModal}
                               className={`${classes.modalButton} ${classes.cancelButton}`}
                           >
                               Annuler
                           </Button>
                           <Button
                               onClick={() => {
                                   setLoading(true);
                                   
                                   const updateData = {
                                       container_id: selectedContainer.id,
                                       etablissement_id: establishmentId,
                                       update_container_info: true,
                                       name: editName,
                                       brand: editBrand,
                                       volume: editVolume,
                                       material: editMaterial,
                                       price: editPrice
                                   };
                                   
                                   if (stockChange !== 0) {
                                       updateData.quantity_change = stockChange;
                                   }
                                   
                                   fetch('https://backend.csibon.app/container_chr/inventory/update', {
                                       method: 'POST',
                                       headers: {
                                           'Content-Type': 'application/json',
                                       },
                                       body: JSON.stringify(updateData),
                                   })
                                   .then(response => {
                                       if (!response.ok) {
                                           throw new Error('Erreur réseau lors de la mise à jour');
                                       }
                                       return response.json();
                                   })
                                   .then(result => {
                                       if (result.container) {
                                           setContainers(containers.map(container => 
                                               container.id === selectedContainer.id ? result.container : container
                                           ));
                                       }
                                       
                                       setSnackbarMessage('Contenant mis à jour avec succès !');
                                       setSnackbarOpen(true);
                                       handleCloseStockModal();
                                   })
                                   .catch(error => {
                                       console.error('Erreur lors de la mise à jour:', error);
                                       setSnackbarMessage('Erreur lors de la mise à jour');
                                       setSnackbarOpen(true);
                                   })
                                   .finally(() => {
                                       setLoading(false);
                                   });
                               }}
                               disabled={loading}
                               className={`${classes.modalButton} ${classes.saveButton} ${classes.progressButton}`}
                           >
                               {loading ? (
                                   <>
                                       <CircularProgress size={24} className={classes.buttonProgress} />
                                       Enregistrement...
                                   </>
                               ) : (
                                   "Enregistrer les modifications"
                               )}
                           </Button>
                       </div>
                   </div>
               </Modal>

               {/* Snackbar pour les notifications */}
               <Snackbar
                   open={snackbarOpen}
                   autoHideDuration={6000}
                   onClose={handleCloseSnackbar}
                   message={snackbarMessage}
                   anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                   style={{
                       '& .MuiSnackbarContent-root': {
                           backgroundColor: theme.palette.primary.main,
                           color: 'white',
                           borderRadius: theme.shape.borderRadius,
                           fontWeight: 500,
                       }
                   }}
               />
           </div>
       </ThemeProvider>
   );
}

export default CreateContainerComponent;